import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Loader2 } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { AppCard } from '../../components/ui/AppCard';
import { IconButton } from '../../components/ui/IconButton';
import { SyncButton } from '../../components/ui/SyncButton';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { useAvatarImageUrl } from '../../components/ui/AvatarView';
import { useInfiniteScroll } from '../../lib/useInfiniteScroll';
import { fetchTemplatesSummary, type TemplatesSummary } from '../../api/dataApi';
import type { Template } from '../../types';
import { usePersistedState, oneOf } from '../../lib/usePersistedState';

function TemplateCard({ template, to, usage }: { template: Template; to: string; usage?: { signals: number; channels: number } }) {
  const imageUrl = useAvatarImageUrl(template.avatarImageKey);
  const signals = usage?.signals ?? template.signals.length;
  const channels = usage?.channels ?? 0;
  return (
    <Link to={to}>
      <AppCard className="flex items-center gap-3">
        <div
          className="w-[60px] h-[60px] rounded-2xl flex items-center justify-center text-3xl shrink-0 overflow-hidden"
          style={{ background: imageUrl ? undefined : `${template.colorHex}26` }}
        >
          {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : template.emoji}
        </div>
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate">{template.title}</span>
            {template.isPublic && (
              <span className="shrink-0 rounded-full bg-blue-600/15 text-blue-600 dark:text-blue-400 text-[11px] font-medium px-2 py-0.5">
                Public
              </span>
            )}
          </div>
          <span className="text-sm text-black/50 dark:text-white/50 truncate">{template.nickname}</span>
          <span className="text-xs text-black/40 dark:text-white/40">
            {signals} signal{signals === 1 ? '' : 's'} · {template.likes} like{template.likes === 1 ? '' : 's'} ·{' '}
            {channels === 0 ? 'unused' : `used in ${channels} channel${channels === 1 ? '' : 's'}`}
          </span>
        </div>
      </AppCard>
    </Link>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex-1 flex flex-col items-center gap-0.5">
      <span className="font-semibold tabular-nums">{value}</span>
      <span className="text-xs text-black/50 dark:text-white/50">{label}</span>
    </div>
  );
}

export function TemplatesListScreen() {
  const { templates, isTemplateOwner, syncTemplates, loadMoreTemplates, templatesHasMore, manualRefresh } = useStore();
  const [tab, setTab] = usePersistedState<'private' | 'public'>('templates.tab', 'private', oneOf(['private', 'public'] as const));
  // Refetch the template list on every visit - the store otherwise only
  // syncs it once per app load. syncTemplates forces the network and
  // applies the result non-destructively; identity is unstable, so run
  // once on mount. Skipped in manual-refresh mode (Settings > Connection);
  // the Sync button below is shown there instead.
  useEffect(() => {
    if (manualRefresh) return;
    void syncTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // System-wide roll-up (GET /stats/templates/summary) - the header
  // totals and each card's "used in N channels" count. Refetched whenever
  // this screen mounts (unless manual-refresh mode is on).
  const [summary, setSummary] = useState<TemplatesSummary | null>(null);
  useEffect(() => {
    if (manualRefresh) return;
    let cancelled = false;
    fetchTemplatesSummary().then((s) => {
      if (!cancelled) setSummary(s);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [publicQuery, setPublicQuery] = useState('');
  // Both tabs filter client-side over whatever page of `templates` has
  // loaded so far - the sentinel below loads more of that underlying,
  // backend-paginated list regardless of which tab is active.
  const sentinelRef = useInfiniteScroll(loadMoreTemplates, templatesHasMore);

  // "My own" means everything this account owns, public or private - not
  // just the private ones. The backend lists every template on the shared
  // instance with no per-owner scoping, so without the ownership check a
  // private template belonging to someone else (or an earlier test
  // session) would show up here too, and clicking it would bounce you
  // straight to the read-only view since you can't actually edit it. A
  // template that's both mine and public still needs to show here (not
  // just in the Public tab) so its owner can find and edit it - the
  // "Public" badge on the card is what disambiguates it from a private one.
  const privateTemplates = templates.filter((t) => isTemplateOwner(t));
  const publicTemplates = templates.filter(
    (t) =>
      t.isPublic &&
      (publicQuery.length < 3 ||
        t.title.toLowerCase().includes(publicQuery.toLowerCase()) ||
        t.nickname.toLowerCase().includes(publicQuery.toLowerCase()))
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <SectionTitle title="Schemas" subtitle="Private and public signal layouts" />
        <div className="flex-1" />
        {manualRefresh && (
          <SyncButton
            onSync={async () => {
              await syncTemplates();
              const s = await fetchTemplatesSummary().catch(() => null);
              if (s) setSummary(s);
            }}
            ariaLabel="Sync schemas"
          />
        )}
        <Link to="/templates/new">
          <IconButton icon={Plus} variant="filled" />
        </Link>
      </div>

      <SegmentedControl
        options={[
          { value: 'private', label: 'My own' },
          { value: 'public', label: 'Public' },
        ]}
        value={tab}
        onChange={setTab}
      />

      {summary && (
        <AppCard className="flex">
          <SummaryStat label="Schemas" value={summary.total} />
          <SummaryStat label="Public" value={summary.publicCount} />
          <SummaryStat label="Private" value={summary.privateCount} />
          <SummaryStat label="Signals" value={summary.totalSignals} />
        </AppCard>
      )}

      {tab === 'private' ? (
        <div className="flex flex-col gap-3">
          {privateTemplates.map((t) => (
            <TemplateCard key={t.id} template={t} to={`/templates/${t.id}/edit`} usage={summary?.byTemplate[t.nickname]} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <input
            value={publicQuery}
            onChange={(e) => setPublicQuery(e.target.value)}
            placeholder="Search public schemas"
            className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
          />
          {publicTemplates.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              to={isTemplateOwner(t) ? `/templates/${t.id}/edit` : `/templates/${t.id}`}
              usage={summary?.byTemplate[t.nickname]}
            />
          ))}
        </div>
      )}

      {templates.length > 0 && templatesHasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          <Loader2 size={20} className="animate-spin text-black/30 dark:text-white/30" />
        </div>
      )}
    </div>
  );
}
